import { useState, useEffect } from "react";
import {
  SwipeableDrawer,
  Dialog,
  Button,
  FilledInput,
  FormControl,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { submitFeedback } from "../../utils/web3forms";
import { addToastSwipeToDismiss } from "../../utils/utils";
import { useAppContext } from "../../utils/AppContext";

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  showCloseButton: true,
  timer: 10000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
    addToastSwipeToDismiss();
  },
});

const TITLES = {
  general: "Send Feedback",
  "missing-root": "Report Missing Root",
  "report-error": "Report an Error",
};

const TYPE_OPTIONS = {
  general: ["General Feedback", "Bug Report"],
  "report-error": ["Wrong Translation", "Missing Entry", "Typo", "Other"],
};

const FeedbackDialog = ({ open, onClose, mode, prefillRoot }) => {
  const { userData } = useAppContext();
  const [feedbackType, setFeedbackType] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isEmailAutoFilled, setIsEmailAutoFilled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setFeedbackType("");
      setMessage(mode === "missing-root" && prefillRoot ? `Missing root: ${prefillRoot}` : "");
      setSubmitting(false);
      setError(null);

      const autoEmail = userData?.email || localStorage.getItem("feedback_email") || "";
      setEmail(autoEmail);
      setIsEmailAutoFilled(!!autoEmail);
    }
  }, [open, mode, prefillRoot, userData]);

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError("Message is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback(mode, {
        feedback_type: feedbackType || mode,
        message,
        root: prefillRoot || "(none)",
        ...(email.trim() && { email: email.trim() }),
      });
      if (email.trim()) {
        localStorage.setItem("feedback_email", email.trim());
      }
      onClose();
      Toast.fire({ icon: "success", title: "Thanks for the feedback!" });
    } catch {
      setError("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const hasTypeDropdown = mode === "general" || mode === "report-error";
  const typeOptions = TYPE_OPTIONS[mode] || [];

  const inputSx = {
    backgroundColor: "#f5f5f5",
    borderRadius: 2,
    fontFamily: "Mulish, sans-serif",
    "& .MuiFilledInput-input": { py: 1.5, px: 2, fontFamily: "Mulish, sans-serif" },
  };

  const formBody = (
    <>
      {mode === "report-error" && prefillRoot && (
        <Box sx={{ display: "inline-flex", alignSelf: "flex-start", bgcolor: "rgba(21,53,189,0.06)", borderRadius: 2, px: 1.5, py: 0.75 }}>
          <Typography sx={{ fontSize: "0.82rem", color: "#1535BD", fontWeight: 600, fontFamily: "Mulish, sans-serif" }}>
            Root: {prefillRoot}
          </Typography>
        </Box>
      )}

      {hasTypeDropdown && (
        <FormControl fullWidth variant="filled">
          <Select
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value)}
            disableUnderline
            displayEmpty
            renderValue={(v) => v || <span style={{ color: "#aaa", fontFamily: "Mulish, sans-serif" }}>Select type…</span>}
            sx={{
              ...inputSx,
              "& .MuiSelect-select": { py: 1.5, px: 2, fontFamily: "Mulish, sans-serif" },
              "&:before, &:after": { display: "none" },
            }}
          >
            {typeOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontFamily: "Mulish, sans-serif" }}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FilledInput
        value={message}
        onChange={(e) => { setMessage(e.target.value); setError(null); }}
        multiline
        rows={4}
        disableUnderline
        fullWidth
        placeholder="Your message…"
        sx={{
          ...inputSx,
          // MUI also pads a multiline root; keep just the inner padding, like the email field.
          p: 0,
          boxShadow: error ? "0 0 0 1.5px #d32f2f" : "none",
          transition: "box-shadow 0.15s",
        }}
      />

      <FilledInput
        value={email}
        onChange={(e) => { setEmail(e.target.value); setIsEmailAutoFilled(false); }}
        disableUnderline
        fullWidth
        type="email"
        placeholder="Your email (optional)"
        sx={inputSx}
        endAdornment={
          isEmailAutoFilled ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                aria-label="Clear email"
                onClick={() => { setEmail(""); setIsEmailAutoFilled(false); }}
                sx={{ mr: 0.5, color: "#bbb", "&:hover": { color: "#555" } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      />

      {error && (
        <Typography sx={{ color: "#d32f2f", fontSize: "0.8rem", fontFamily: "Mulish, sans-serif", mt: -0.5 }}>
          {error}
        </Typography>
      )}
    </>
  );

  const submitButton = (
    <Button
      variant="contained"
      fullWidth
      onClick={handleSubmit}
      disabled={submitting}
      sx={{
        borderRadius: "20px",
        textTransform: "none",
        fontWeight: 700,
        fontFamily: "Mulish, sans-serif",
        fontSize: "0.95rem",
        py: 1.25,
        bgcolor: "#1535BD",
        boxShadow: "none",
        "&:hover": { bgcolor: "#0F2EA8", boxShadow: "none" },
        "&.Mui-disabled": { bgcolor: "rgba(21,53,189,0.35)", color: "white" },
      }}
    >
      {submitting ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Submit"}
    </Button>
  );

  if (isMobile) {
    return (
      // Swipeable so the drag handle works. Not kept mounted: a hidden .MuiModal-root
      // reads as an open dialog to the hotkeys.
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        disableSwipeToOpen
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            px: 1,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: "rgba(0,0,0,0.13)" }} />
        </Box>
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Box sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#888", fontFamily: "Mulish, sans-serif", letterSpacing: "0.04em", textTransform: "uppercase", userSelect: "none" }}>
            {TITLES[mode]}
          </Box>
        </Box>
        <Box sx={{ px: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {formBody}
        </Box>
        <Box sx={{ px: 2, pt: 2 }}>
          {submitButton}
          <Button
            fullWidth
            onClick={onClose}
            disabled={submitting}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 600,
              fontFamily: "Mulish, sans-serif",
              color: "#888",
              mt: 0.5,
              "&:hover": { bgcolor: "transparent", color: "#555" },
            }}
          >
            Cancel
          </Button>
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: "1px solid rgba(21,53,189,0.08)",
          boxShadow: "0px 20px 60px rgba(21,53,189,0.12)",
          fontFamily: "Mulish, sans-serif",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 3, pb: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: "#1a1a1a", fontFamily: "Mulish, sans-serif" }}>
          {TITLES[mode]}
        </Typography>
        <IconButton size="small" aria-label="Close" onClick={onClose} sx={{ color: "#bbb", "&:hover": { color: "#555", bgcolor: "rgba(0,0,0,0.05)" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: 3, pt: 1.5, pb: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {formBody}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, py: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{
            borderRadius: "20px",
            textTransform: "none",
            fontWeight: 600,
            fontFamily: "Mulish, sans-serif",
            color: "#888",
            "&:hover": { bgcolor: "rgba(0,0,0,0.04)", color: "#555" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            borderRadius: "20px",
            textTransform: "none",
            fontWeight: 700,
            fontFamily: "Mulish, sans-serif",
            bgcolor: "#1535BD",
            boxShadow: "none",
            px: 3,
            minWidth: 90,
            "&:hover": { bgcolor: "#0F2EA8", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "rgba(21,53,189,0.35)", color: "white" },
          }}
        >
          {submitting ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Submit"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default FeedbackDialog;
